// app/javascript/components/RealTimeChat.vue
<template>
  <div class="grid grid-cols-1 md:grid-cols-4 gap-6 w-full mx-auto h-[750px] py-2">

    <!-- Left Sidebar: Chat Meta / History (Tailwind Design) -->
    <div
        class="hidden md:flex flex-col justify-between bg-base-200/50 backdrop-blur-md rounded-2xl p-4 border border-base-300">
      <div class="space-y-4">
        <div class="badge badge-success gap-2 py-3 px-4 font-semibold">
          <span class="w-2 h-2 rounded-full bg-base-100 animate-pulse"></span>
          Connected Live
        </div>
        <h2 class="text-xl font-bold tracking-tight text-base-content px-2">
         {{ inspection?.title }}</h2>
        <span class='text-xs text-base-content/60'>
         {{ inspection?.created_at }}
        </span>
        <p class="text-xs text-base-content/60 leading-relaxed">
          {{inspection}}
        </p>
      </div>
     <!-- <div class="text-xs opacity-40 px-2">Session: {{ route.params.ai_conversation_id }}</div>-->
    </div>

    <!-- Right Window: Main Chat Thread -->
    <div
        class="md:col-span-3 flex flex-col h-full bg-base-100 border border-base-300 rounded-2xl overflow-hidden">

      <!-- Top Thread Bar -->
      <div class="navbar bg-base-100 border-b border-base-300 px-6 justify-between flex-none">
        <div class="flex items-center gap-3">
          <div class="avatar bg-black rounded-full p-2 flex items-center justify-center">
            <div class="text-xs">AI</div>
          </div>
          <div>
            <h3 class="font-bold text-sm">{{ inspection?.name }}</h3>
            <span class="text-xs text-success flex items-center gap-1">
              {{ !data.isTyping ? 'Ready to stream' : 'Processing...' }}
            </span>
          </div>
        </div>
      </div>
      <!-- Real-Time Chat Bubble Viewport -->
      <div ref="scrollArea" class="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-base-100 to-base-200/30">
        <div
            v-for="msg in data.messages"
            :key="msg.id"

        >
          <div :class="['chat', msg.role === 'user' ? 'chat-end' : 'chat-start']">
            <div :class="[
            'chat-bubble font-medium shadow-sm max-w-md md:max-w-lg', 
            msg.role === 'user'
              ? 'chat-bubble-info text-primary-content rounded-br-none'
              : 'chat-bubble-neutral text-neutral-content rounded-bl-none'
          ]">
              <span class="text-xs opacity-40">{{ msg.role === 'user' ? '' : 'AI Agent' }}</span>
              <div>
                {{ msg.content }}
              </div>

              <div class="flex py-2 my-2 flex-wrap" v-if="msg.actions && msg.actions.length > 0">
                <button v-for="action in msg.actions" :key="action.id"
                        @click.prevent="submitAction(action.action)"
                        class="badge badge-default px-2 py-1 rounded-md mr-2 mb-2">{{ action.label }}
                </button>
              </div>
              <div class="text-xs opacity-40 my-2" v-if="msg.created_at">{{ msg.created_at }}</div>
            </div>

          </div>
        </div>
        <!-- Live Loading Indicator Indicator -->
        <div v-if="data.isTyping" class="chat chat-start">
          <div class="chat-bubble chat-bubble-neutral py-3 rounded-bl-none">
            <span class="loading loading-dots loading-md block"></span>
          </div>
        </div>
      </div>

      <!-- Input Message Form -->
      <div class="p-4 bg-base-100 border-t border-base-300 flex-none">
        <form @submit.prevent="submitMessage" class="join w-full">
          <input
              v-model="data.inputMessage"
              type="text"
              placeholder="Type a query to stream response..."
              class="input input-bordered join-item w-full focus:outline-none focus:border-primary px-4 bg-base-200/50"
              :disabled="data.isTyping"
          />
          <button
              type="submit"
              class="btn btn-primary join-item px-6"
              :disabled="!data.inputMessage.trim() || data.isTyping"
          >
            Send
          </button>
        </form>
      </div>

    </div>
  </div>
</template>

<script setup>
import {createConsumer} from "@rails/actioncable";
import {nextTick, onMounted, reactive, ref, useTemplateRef} from "vue";
import {fetchAiInspectionInfo, sendAiInspectionMessage, fetchAiInspectionMessages} from "~/lib/api/ai_inspections";
import {useRoute} from "vue-router";

let route = useRoute();
let subscriptionChannel;

const scrollArea = useTemplateRef('scrollArea');
let inspection = ref(null);
const data = reactive({
  inputMessage: "",
  isTyping: false,
  channel: null,
  messages: []
})
onMounted(() => {
  initActionCable();
  loadAiInspection();
});

const initActionCable = () => {
  const consumer = createConsumer();
  subscriptionChannel = consumer.subscriptions.create(
      {
        channel: "AiChatChannel",
        ai_inspection_id: route.params.ai_inspection_id
      },
      {
        received: ({payload}) => {
          data.isTyping = false;
          console.log("AiChatChannel :: Received message:", data.messages.length);
          if (payload.type === "typing") {
            data.isTyping = true;
          } else {
            if (payload.type === "thinking") {
              data.isTyping = true;
            } else {
              data.messages.push({
                id: Date.now() + Math.random(),
                role: payload.role,
                content: payload.content,
                actions: payload.actions,
                created_at: payload.created_at
              });
            }
          }
          console.log("AiChatChannel :: Received message:", data.messages.length);
          scrollToBottom();
        },
        connected: () => {
          console.log("AiChatChannel :: Connected to ActionCable");
          console.log("AiChatChannel :: Connected to ActionCable");
          console.log("AiChatChannel :: Connected to ActionCable");
          console.log("AiChatChannel :: Connected to ActionCable");
          console.log("AiChatChannel :: Connected to ActionCable");
          console.log("AiChatChannel :: Connected to ActionCable");
        },
        disconnected: () => {
          console.log("AiChatChannel :: Disconnected from ActionCable");
        },
      }
  );
};
const loadAiInspection = async () => {
  let r = await fetchAiInspectionInfo(route.params.organization_uuid, route.params.ai_inspection_id);
  console.log(r);
  inspection.value = r.data.inspection;
  // after loading inspection & conversation
  await loadMessages();
}

const loadMessages = async () => {
  let r = await fetchAiInspectionMessages(
      route.params.organization_uuid,
      route.params.ai_inspection_id,
      getConversationId()
  );
  console.log(r.data.messages);
  console.log(r.data.messages);
  console.log(r.data.messages);
  console.log(r.data.messages);
  data.messages = data.messages.concat(r.data.messages);
}

const getConversationId = () => inspection.value.ai_conversation.id;
const submitAction = (action) => {
  submitMessage(null, action, action)
}
const submitMessage = (event, content, action) => {
  console.log(content);
  let message = content || data.inputMessage.trim();
  if (!message) return;
  data.isTyping = true;

  data.inputMessage = "";

  subscriptionChannel.send({
    ai_conversation_id: getConversationId(),
    message: {
      content: message,
      action: action
    }
  });
  return ;
  sendAiInspectionMessage(
      route.params.organization_uuid,
      route.params.ai_inspection_id,
      getConversationId(),
      {
        'ai_message': {
          content: message,
          action: action
        }
      });
  scrollToBottom();
};


const scrollToBottom = () => {
  nextTick(() => {
    const el = scrollArea.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
}
</script>
